import { lazy } from 'react';

import { Navigate } from 'react-router-dom';

import DocumentParser from './Pages/DocumentParser';
// import { COGSUpload } from './Pages/Masters/Cogs';
import CogsListing from './Pages/Masters/Cogs/Listing';
import AsyncCogsUpload from './Pages/Masters/Cogs/Upload copy';
import { SKUTPBulkUpload, SKUTPListing } from './Pages/Masters/TransferPricing';
import AsyncTpUploader from './Pages/Masters/TransferPricing/Upload copy';
import ReturnDetails from './Pages/Sales/Return/return-details';
import ReturnDetailsDelivered from './Pages/Sales/Return/return-details-delivered';
import Welcome from './Pages/Welcome';
import { USER_ACCESS_MODES } from './Utils/userAccessPolicy';

const Listing = lazy(() => import('./Pages/purchase-request/Listing'));
const PRDetails = lazy(() => import('./Pages/Purchases/PurchaseRequest/pr-details'));
const ASNCreate = lazy(() => import('./Pages/Purchases/ASN/asn-create'));
const ASNDetails = lazy(() => import('./Pages/Purchases/ASN/asn-details'));
const GRNCreate = lazy(() => import('./Pages/Purchases/GRN/grn-create'));
const GRNView = lazy(() => import('./Pages/Purchases/GRN/grn-view'));
const PODetails = lazy(() => import('./Pages/Purchases/PurchaseOrder/po-details'));
const PRCreate = lazy(() => import('./Pages/Purchases/PurchaseRequest/pr-create'));
const POAllocate = lazy(() => import('./Pages/Sales/CustomerPO/customer-po-allocate'));
const CustomerPOCreate = lazy(() => import('./Pages/Sales/CustomerPO/customer-po-create'));
const CustomerPODetailsWrapper = lazy(() => import('./Pages/Sales/CustomerPO/customer-po-details-wrapper'));
const SOGRNCreate = lazy(() => import('./Pages/Sales/DomesticGRN/so-grn-create'));
const SoGRNView = lazy(() => import('./Pages/Sales/DomesticGRN/so-grn-view'));
const SalesOrderCreate = lazy(() => import('./Pages/Sales/DomesticSalesOrder/sales-order-create'));
const SalesOrderDetails = lazy(() => import('./Pages/Sales/DomesticSalesOrder/sales-order-details'));
const SalesOrderDetailsDelivered = lazy(() => import('./Pages/Sales/DomesticSalesOrder/sales-order-details-delivered'));
const CustomerBillsListing = lazy(() => import('./Pages/Sales/CustomerBills/CustomerBillsListing'));
const CustomerBillsDetails = lazy(() => import('./Pages/Sales/CustomerBills/CustomerBillsDetails'));
const InternationalGrnCreate = lazy(() => import('./Pages/Sales/InternationalGRN/international-grn-create'));
const InternationalGrnView = lazy(() => import('./Pages/Sales/InternationalGRN/international-grn-view'));
const ROGrnView = lazy(() => import('./Pages/Sales/ReturnOrderGRN/ro-grn-view'));
const InternationalSoCreate = lazy(() => import('./Pages/Sales/InternationalSalesOrder/international-so-create'));
const WarehouseShippingDetailsUpdate = lazy(
  () => import('./Pages/Sales/InternationalSalesOrder/warehouse-shipping-details-update')
);
const InternationalSoDetails = lazy(() => import('./Pages/Sales/InternationalSalesOrder/international-so-details'));
const InternationalSoDetailsDelivered = lazy(
  () => import('./Pages/Sales/InternationalSalesOrder/international-so-details-delivered')
);
const ROCreate = lazy(() => import('./Pages/Sales/ReturnOrder/ROCreate'));
const ROView = lazy(() => import('./Pages/Sales/ReturnOrder/ROView'));
const ReturnOrderBulkUpload = lazy(() => import('./Pages/Sales/BulkActions/ReturnOrderBulkUpload'));
const BulkEpodManualUpload = lazy(() => import('./Pages/Sales/BulkActions/BulkEpodManualUpload'));
const BulkAppointmentDownload = lazy(() => import('./Pages/Sales/BulkActions/BulkAppointmentDownload'));
const PurchaseROCreate = lazy(() => import('./Pages/Purchases/ReturnOrder/ro-create'));
const PurchaseRODetails = lazy(() => import('./Pages/Purchases/ReturnOrder/ro-details'));
const PurchaseRODetailsDelivered = lazy(() => import('./Pages/Purchases/ReturnOrder/ro-details-delivered'));
const PRCreateInterEntity = lazy(() => import('./Pages/Purchases/PurchaseRequest/pr-create-inter-entity'));
const UARListing = lazy(() => import('./Pages/UserAccessReview/UARListing'));
const UARDetails = lazy(() => import('./Pages/UserAccessReview/UARDetails'));
const PrintTag = lazy(() => import('./Pages/Sales/PrintTag/PrintTag'));
const CreateReview = lazy(() => import('./Pages/UserAccessReview/CreateReview'));
const VendorBillListing = lazy(() => import('./Pages/Purchases/VendorBills/VendorBillListing'));
const VendorBillDetails = lazy(() => import('./Pages/Purchases/VendorBills/VendorBillDetails'));
const DebitNoteListing = lazy(() => import('./Pages/Purchases/DebitNotes/DebitNoteListing'));
const DebitNoteDetails = lazy(() => import('./Pages/Purchases/DebitNotes/DebitNoteDetails'));

const routeGuard = (ifVendor, component, isVendorAllowed) => {
  if (!ifVendor) return component;
  if (ifVendor && isVendorAllowed) return component;
  return <Navigate to="/" />;
};

const routeMap = (accessProfile, groups) => {
  if (accessProfile.mode === USER_ACCESS_MODES.NONE) {
    return [];
  }

  if (accessProfile.mode === USER_ACCESS_MODES.PRINT_ONLY) {
    return [
      {
        key: 'default',
        path: '/',
        value: <Navigate to={accessProfile.landingPath} />
      },
      {
        key: 'print-tag',
        path: '/sales/print-tag',
        value: <PrintTag />
      }
    ];
  }

  const isVendor = accessProfile.mode === USER_ACCESS_MODES.VENDOR;

  return [
    {
      key: 'default',
      path: '/',
      value: <Navigate to={accessProfile.landingPath} />
    },
    {
      key: 'welcome',
      path: '/welcome',
      value: <Welcome />
    },
    {
      key: 'create-purchase-request',
      path: '/purchases/purchase-request/:type/:action/:id?',
      value: routeGuard(isVendor, <PRCreate />, false)
    },
    {
      key: 'create-purchase-request-inter-entity',
      path: '/purchases/purchase-request-inter-entity/:type/:action/:id?/:brandId?/:vendorId?/:customerPoNumber?',
      value: routeGuard(isVendor, <PRCreateInterEntity />, false)
    },
    {
      key: 'listing-purchase-request',
      path: '/purchases/purchase-request',
      value: routeGuard(isVendor, <Listing type="por" />, false)
    },
    {
      key: 'details-purchase-request',
      path: '/purchases/purchase-request/:id?',
      value: routeGuard(isVendor, <PRDetails />, false)
    },
    {
      key: 'listing-purchase-order',
      path: '/purchases/purchase-order',
      value: routeGuard(isVendor, <Listing type="po" />, false)
    },
    {
      key: 'details-purchase-order',
      path: '/purchases/purchase-order/:id',
      value: routeGuard(isVendor, <PODetails />, false)
    },
    {
      key: 'create-asn',
      path: '/purchases/asn/:type/create',
      value: routeGuard(isVendor, <ASNCreate ifVendor={isVendor} />, true)
    },
    {
      key: 'edit-asn',
      path: '/purchases/asn/:type/edit/:id?',
      value: routeGuard(isVendor, <ASNCreate ifVendor={isVendor} />, true)
    },
    {
      key: 'listing-asn',
      path: '/purchases/asn',
      value: routeGuard(isVendor, <Listing type="po_asn" />, true)
    },
    {
      key: 'details-asn',
      path: '/purchases/asn/:id',
      value: routeGuard(isVendor, <ASNDetails />, true)
    },
    {
      key: 'create-grn',
      path: '/purchases/grn/:type/create',
      value: routeGuard(isVendor, <GRNCreate />, false)
    },
    {
      key: 'edit-grn',
      path: '/purchases/grn/:type/edit/:id?',
      value: routeGuard(isVendor, <GRNCreate />, false)
    },
    {
      key: 'listing-grn',
      path: '/purchases/grn',
      value: routeGuard(isVendor, <Listing type="po_grn" />, false)
    },
    {
      key: 'view-grn',
      path: '/purchases/grn/:id',
      value: routeGuard(isVendor, <GRNView ifVendor={isVendor} />, false)
    },
    {
      key: 'listing-vendor-bills',
      path: '/purchases/vendor-bills',
      value: routeGuard(isVendor, <VendorBillListing />, false)
    },
    {
      key: 'details-vendor-bills',
      path: '/purchases/vendor-bills/:id',
      value: routeGuard(isVendor, <VendorBillDetails />, false)
    },
    {
      key: 'listing-debit-notes',
      path: '/purchases/debit-notes',
      value: routeGuard(isVendor, <DebitNoteListing />, false)
    },
    {
      key: 'details-debit-notes',
      path: '/purchases/debit-notes/:id',
      value: routeGuard(isVendor, <DebitNoteDetails />, false)
    },
    {
      key: 'listing-inventory',
      path: '/inventory',
      value: <Listing type="inventory" />
    },
    {
      key: 'create-customer-po',
      path: '/sales/customer-po/:type/create',
      value: <CustomerPOCreate />
    },
    {
      key: 'edit-customer-po',
      path: '/sales/customer-po/:type/edit/:id',
      value: <CustomerPOCreate />
    },
    {
      key: 'listing-customer-po',
      path: '/sales/customer-po',
      value: <Listing type="s_cpo" />
    },
    {
      key: 'details-customer-po',
      path: '/sales/customer-po/:id',
      value: <CustomerPODetailsWrapper />
    },
    {
      key: 'allocate-customer-po',
      path: '/sales/customer-po/:id/allocate',
      value: <POAllocate />
    },
    {
      key: 'create-domestic-sales-order',
      path: '/sales/domestic-sales-order/:type/create',
      value: <SalesOrderCreate />
    },
    {
      key: 'edit-domestic-sales-order',
      path: '/sales/domestic-sales-order/:type/edit/:id',
      value: <SalesOrderCreate />
    },
    {
      key: 'listing-domestic-sales-order',
      path: '/sales/domestic-sales-order',
      value: <Listing type="s_dso" />
    },
    {
      key: 'details-domestic-sales-order',
      path: '/sales/domestic-sales-order/:id',
      value: <SalesOrderDetails ifVendor={isVendor} />
    },
    {
      key: 'delivery-details-domestic-sales-order',
      path: '/sales/domestic-sales-order/:id/delivered',
      value: <SalesOrderDetailsDelivered ifVendor={isVendor} />
    },
    {
      key: 'listing-customer-bills',
      path: '/sales/customer-bills',
      value: <CustomerBillsListing />
    },
    {
      key: 'details-customer-bills',
      path: '/sales/customer-bills/:id',
      value: <CustomerBillsDetails />
    },
    {
      key: 'create-international-sales-order',
      path: '/sales/international-sales-order/:type/create',
      value: <InternationalSoCreate />
    },
    {
      key: 'edit-international-sales-order',
      path: '/sales/international-sales-order/:type/edit/:id',
      value: <InternationalSoCreate />
    },
    {
      key: 'warehouse-shipping-details-update',
      path: '/sales/international-sales-order/warehouse-details-update/:id',
      value: <WarehouseShippingDetailsUpdate />
    },
    {
      key: 'listing-international-sales-order',
      path: '/sales/international-sales-order',
      value: <Listing type="s_iso" />
    },
    {
      key: 'details-international-sales-order',
      path: '/sales/international-sales-order/:id',
      value: <InternationalSoDetails ifVendor={isVendor} />
    },
    {
      key: 'delivery-details-international-sales-order',
      path: '/sales/international-sales-order/:id/delivered',
      value: <InternationalSoDetailsDelivered ifVendor={isVendor} />
    },
    {
      key: 'create-domestic-grn',
      path: '/sales/domestic-grn/:type/create',
      value: <SOGRNCreate />
    },
    {
      key: 'edit-domestic-grn',
      path: '/sales/domestic-grn/:type/edit/:id',
      value: <SOGRNCreate />
    },
    {
      key: 'listing-domestic-grn',
      path: '/sales/domestic-grn',
      value: <Listing type="so_dgrn" />
    },
    {
      key: 'details-domestic-grn',
      path: '/sales/domestic-grn/:id',
      value: <SoGRNView ifVendor={isVendor} />
    },
    {
      key: 'create-international-grn',
      path: '/sales/international-grn/:type/create',
      value: <InternationalGrnCreate />
    },
    {
      key: 'edit-international-grn',
      path: '/sales/international-grn/:type/edit/:id',
      value: <InternationalGrnCreate />
    },
    {
      key: 'listing-international-grn',
      path: '/sales/international-grn',
      value: <Listing type="so_igrn" />
    },
    {
      key: 'details-international-grn',
      path: '/sales/international-grn/:id',
      value: <InternationalGrnView ifVendor={isVendor} />
    },
    {
      key: 'create-ro',
      path: 'sales/ro/create/:type',
      value: <ROCreate />
    },
    {
      key: 'listing-ro',
      path: 'sales/ro',
      value: <Listing type="ro" />
    },
    {
      key: 'details-ro',
      path: 'sales/ro/:id',
      value: <ROView />
    },
    {
      key: 'return_order_bulk_upload',
      path: '/sales/bulk-actions/return-order-create',
      value: <ReturnOrderBulkUpload />
    },
    {
      key: 'bulk_epod_manual_upload',
      path: '/sales/bulk-actions/epod-manual-upload',
      value: <BulkEpodManualUpload />
    },
    {
      key: 'bulk_appointment_download',
      path: '/sales/bulk-actions/appointment-files-download',
      value: <BulkAppointmentDownload />
    },
    {
      key: 'details-return',
      path: 'sales/return/:id',
      value: <ReturnDetails />
    },
    {
      key: 'details-return',
      path: 'sales/return/:id/delivered',
      value: <ReturnDetailsDelivered />
    },
    ...(accessProfile.canAccessPrintTag
      ? [
          {
            key: 'print-tag',
            path: '/sales/print-tag',
            value: <PrintTag />
          }
        ]
      : []),
    {
      key: 'details-ro-grn',
      path: 'sales/ro-grn/:id',
      value: <ROGrnView />
    },
    {
      key: 'master_cogs',
      path: 'masters/cogs',
      value: <CogsListing />
    },
    {
      key: 'master_cogs_upload',
      path: 'masters/cogs/bulk/:action/:id?',
      value: <AsyncCogsUpload />
    },
    {
      key: 'master_tp',
      path: 'masters/transfer_pricing',
      value: <SKUTPListing />
    },
    {
      key: 'master_tp_upload',
      path: 'masters/transfer-pricing/bulk/:action/:id?',
      value: <AsyncTpUploader key="master_tp_upload" />
    },
    {
      key: 'master_tp_update',
      path: 'masters/transfer-pricing/bulk/:action/:id?',
      value: <AsyncTpUploader key="master_tp_update" />
    },
    ...(groups?.includes('SUPER_ADMIN') || groups?.includes('RTV_DOMESTIC_BULK_CREATION')
      ? [
          {
            key: 'create-purchase-return-order',
            path: '/purchases/return-order/:type/:id?',
            value: <PurchaseROCreate />
          }
        ]
      : []),
    {
      key: 'listing-purchase-return-order',
      path: '/purchases/return-order',
      value: <Listing type="poReturnOrder" />
    },
    {
      key: 'details-purchase-return-order',
      path: '/purchases/return-order/details/:id?',
      value: <PurchaseRODetails />
    },
    {
      key: 'details-purchase-return-order',
      path: '/purchases/return-order/details/:id/delivered',
      value: <PurchaseRODetailsDelivered />
    },
    {
      key: 'document-parser',
      path: '/document-parser/:type/:id?',
      value: <DocumentParser />
    },
    {
      key: 'uar-listing',
      path: '/access-control/user-access-reviews',
      value: <UARListing />
    },
    {
      key: 'uar-create',
      path: '/access-control/user-access-reviews/create',
      value: <CreateReview />
    },
    {
      key: 'uar-details',
      path: '/access-control/user-access-reviews/:id',
      value: <UARDetails />
    }
  ];
};

export default routeMap;
